import React, { useState, useEffect } from 'react';
import { X, Wrench, Calendar, DollarSign, AlertCircle } from 'lucide-react';
import apiClient from '../../api/config';
import { useToastContext } from '../common/Toast';
import { handleApiError } from '../../utils/errorHandler';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

interface Vehicle {
  id: number;
  customer_id: number;
  make: string;
  model: string;
  year: number;
  plate_number: string;
  mileage?: number;
  customer?: {
    id: number;
    name: string;
    phone_number: string;
    email: string;
  };
}

interface Employee {
  id: number;
  name: string;
  email: string;
  specialization?: string;
  is_available?: boolean;
}

interface CreateJobCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle?: Vehicle;
  onSuccess?: (jobCard: any) => void;
}

const CreateJobCardModal: React.FC<CreateJobCardModalProps> = ({
  isOpen,
  onClose,
  vehicle,
  onSuccess
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { showSuccess, showError } = useToastContext();
  
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  
  const [formData, setFormData] = useState({
    vehicle_id: vehicle?.id || '',
    customer_id: vehicle?.customer_id || '',
    assigned_employee_id: '',
    service_type: '',
    description: '',
    customer_notes: '',
    estimated_cost: 0,
    priority: 'medium',
    estimated_completion_date: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
      if (!vehicle) {
        fetchVehicles();
      }
    }
  }, [isOpen, vehicle]);

  useEffect(() => {
    if (vehicle) {
      setFormData(prev => ({
        ...prev,
        vehicle_id: vehicle.id,
        customer_id: vehicle.customer_id
      }));
    }
  }, [vehicle]);

  const fetchEmployees = async () => {
    try {
      const response = await apiClient.get('/employees', {
        params: { is_active: true, role: 'employee' }
      });
      setEmployees(response.data.data || response.data.employees || []);
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await apiClient.get('/vehicles', {
        params: { page_size: 100 }
      });
      setVehicles(response.data.vehicles || response.data.data || []);
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.vehicle_id) {
      showError('Please select a vehicle');
      return;
    }

    if (!formData.service_type || !formData.description) {
      showError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const jobCardData = {
        ...formData,
        vehicle_id: parseInt(formData.vehicle_id.toString()),
        customer_id: parseInt(formData.customer_id.toString()),
        assigned_employee_id: formData.assigned_employee_id ? parseInt(formData.assigned_employee_id.toString()) : null,
        estimated_cost: parseFloat(formData.estimated_cost.toString()) || 0,
        garage_id: user?.garage_id,
        status: 'pending'
      };

      const response = await apiClient.post('/job-cards', jobCardData);
      
      showSuccess('Job card created successfully');
      
      if (onSuccess) {
        onSuccess(response.data.data || response.data.job_card || response.data);
      }
      
      handleClose();
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      vehicle_id: '',
      customer_id: '',
      assigned_employee_id: '',
      service_type: '',
      description: '',
      customer_notes: '',
      estimated_cost: 0,
      priority: 'medium',
      estimated_completion_date: ''
    });
    onClose();
  };

  const handleVehicleSelect = (vehicleId: string) => {
    const selectedVehicle = vehicles.find(v => v.id.toString() === vehicleId);
    if (selectedVehicle) {
      setFormData(prev => ({
        ...prev,
        vehicle_id: vehicleId,
        customer_id: selectedVehicle.customer_id.toString()
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">
              Create New Job Card
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Vehicle Selection */}
              {vehicle ? (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle
                  </label>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <p className="font-medium">
                      {vehicle.make} {vehicle.model} ({vehicle.year})
                    </p>
                    <p className="text-sm text-gray-600">
                      Plate: {vehicle.plate_number}
                    </p>
                    {vehicle.customer && (
                      <p className="text-sm text-gray-600">
                        Owner: {vehicle.customer.name}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Vehicle <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.vehicle_id}
                    onChange={(e) => handleVehicleSelect(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  >
                    <option value="">Select a vehicle</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.make} {v.model} ({v.year}) - {v.plate_number}
                        {v.customer && ` - ${v.customer.name}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Service Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.service_type}
                  onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                >
                  <option value="">Select service type</option>
                  <option value="Oil Change">Oil Change</option>
                  <option value="Tire Service">Tire Service</option>
                  <option value="Brake Service">Brake Service</option>
                  <option value="Engine Repair">Engine Repair</option>
                  <option value="Transmission Service">Transmission Service</option>
                  <option value="Electrical Repair">Electrical Repair</option>
                  <option value="AC Service">AC Service</option>
                  <option value="General Maintenance">General Maintenance</option>
                  <option value="Inspection">Inspection</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              {/* Assigned Employee */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign to Technician
                </label>
                <select
                  value={formData.assigned_employee_id}
                  onChange={(e) => setFormData({ ...formData, assigned_employee_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Select technician (optional)</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.name}
                      {employee.specialization && ` - ${employee.specialization}`}
                      {employee.is_available === false && ' (Busy)'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Estimated Completion Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Completion Date
                </label>
                <input
                  type="date"
                  value={formData.estimated_completion_date}
                  onChange={(e) => setFormData({ ...formData, estimated_completion_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Estimated Cost */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Cost (SAR)
                </label>
                <input
                  type="number"
                  value={formData.estimated_cost}
                  onChange={(e) => setFormData({ ...formData, estimated_cost: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Problem Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Describe the issues or services needed..."
                  required
                />
              </div>

              {/* Customer Notes */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Notes
                </label>
                <textarea
                  value={formData.customer_notes}
                  onChange={(e) => setFormData({ ...formData, customer_notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Any special requests or notes from the customer..."
                />
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50 transition-colors flex items-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Wrench className="h-4 w-4 mr-2" />
                    Create Job Card
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateJobCardModal;